import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch, apiDownload } from '../../../lib/api';
import BACKEND_URL from '../../../config.js';
import { useSocieta } from '../../../context/SocietaContext';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './LocazioneSchedaPage.api';

type Periodicita = 'MENSILE' | 'BIMESTRALE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';
type ModalitaCauzione = 'BONIFICO' | 'ASSEGNO' | 'CONTANTI' | 'FIDEIUSSIONE_BANCARIA' | 'POLIZZA_ASSICURATIVA';
type TipoDoc = 'CONTRATTO' | 'REGISTRAZIONE' | 'ALLEGATO';

const PERIODICITA_LABEL: Record<Periodicita, string> = {
  MENSILE: 'Mensile',
  BIMESTRALE: 'Bimestrale',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

const MODALITA_CAUZIONE_LABEL: Record<ModalitaCauzione, string> = {
  BONIFICO: 'Bonifico',
  ASSEGNO: 'Assegno',
  CONTANTI: 'Contanti',
  FIDEIUSSIONE_BANCARIA: 'Fideiussione bancaria',
  POLIZZA_ASSICURATIVA: 'Polizza assicurativa',
};

const TIPO_DOC_LABEL: Record<TipoDoc, string> = {
  CONTRATTO: 'Contratto',
  REGISTRAZIONE: 'Registrazione',
  ALLEGATO: 'Allegato',
};

interface SedeMin { id: number; nome: string; citta: string | null }
interface FornitoreMin { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null }

interface DocumentoLocazione {
  id: number; tipo: TipoDoc; descrizione: string | null;
  nomeFile: string; mimeType: string | null; dimensione: number | null;
  uploadedByName: string | null; uploadedAt: string;
}
interface LogLocazione {
  id: number; utenteName: string | null; azione: string; dettaglio: string | null; createdAt: string;
}
interface Locazione {
  id: number;
  societaId: number;
  sedeId: number;
  fornitoreId: number;
  dataInizio: string;
  durataAnniIniziale: number;
  durataAnniProroga: number;
  preavvisoDisdettaMesi: number;
  importoCanone: string | number;
  periodicitaPagamento: Periodicita;
  cauzioneImporto: string | number | null;
  cauzioneModalita: ModalitaCauzione | null;
  cauzioneDataVersamento: string | null;
  cauzioneNote: string | null;
  rivalutazioneIstat: boolean;
  rivalutazioneIstatPerc: string | number | null;
  disdettata: boolean;
  dataDisdetta: string | null;
  noteDisdetta: string | null;
  attivo: boolean;
  note: string | null;
  societa: { id: number; nome: string };
  sede: SedeMin;
  fornitore: FornitoreMin;
  documenti: DocumentoLocazione[];
  log: LogLocazione[];
}

interface FormState {
  societaId: string;
  sedeId: string;
  fornitoreId: string;
  dataInizio: string;
  durataAnniIniziale: string;
  durataAnniProroga: string;
  preavvisoDisdettaMesi: string;
  importoCanone: string;
  periodicitaPagamento: Periodicita;
  cauzioneImporto: string;
  cauzioneModalita: ModalitaCauzione | '';
  cauzioneDataVersamento: string;
  cauzioneNote: string;
  rivalutazioneIstat: boolean;
  rivalutazioneIstatPerc: string;
  disdettata: boolean;
  dataDisdetta: string;
  noteDisdetta: string;
  note: string;
}

function emptyForm(societaId = ''): FormState {
  return {
    societaId,
    sedeId: '',
    fornitoreId: '',
    dataInizio: '',
    durataAnniIniziale: '6',
    durataAnniProroga: '6',
    preavvisoDisdettaMesi: '12',
    importoCanone: '',
    periodicitaPagamento: 'MENSILE',
    cauzioneImporto: '',
    cauzioneModalita: '',
    cauzioneDataVersamento: '',
    cauzioneNote: '',
    rivalutazioneIstat: false,
    rivalutazioneIstatPerc: '75',
    disdettata: false,
    dataDisdetta: '',
    noteDisdetta: '',
    note: '',
  };
}

function isoDate(d?: string | null): string {
  if (!d) return '';
  return d.substring(0, 10);
}

function recordToForm(r: Locazione): FormState {
  return {
    societaId: String(r.societaId),
    sedeId: String(r.sedeId),
    fornitoreId: String(r.fornitoreId),
    dataInizio: isoDate(r.dataInizio),
    durataAnniIniziale: String(r.durataAnniIniziale),
    durataAnniProroga: String(r.durataAnniProroga),
    preavvisoDisdettaMesi: String(r.preavvisoDisdettaMesi),
    importoCanone: r.importoCanone != null ? String(Number(r.importoCanone)) : '',
    periodicitaPagamento: r.periodicitaPagamento,
    cauzioneImporto: r.cauzioneImporto != null ? String(Number(r.cauzioneImporto)) : '',
    cauzioneModalita: r.cauzioneModalita ?? '',
    cauzioneDataVersamento: isoDate(r.cauzioneDataVersamento),
    cauzioneNote: r.cauzioneNote ?? '',
    rivalutazioneIstat: !!r.rivalutazioneIstat,
    rivalutazioneIstatPerc: r.rivalutazioneIstatPerc != null ? String(Number(r.rivalutazioneIstatPerc)) : '',
    disdettata: !!r.disdettata,
    dataDisdetta: isoDate(r.dataDisdetta),
    noteDisdetta: r.noteDisdetta ?? '',
    note: r.note ?? '',
  };
}

function addYears(iso: string, years: number): string {
  if (!iso) return '';
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d.toLocaleDateString('it-IT');
}

function fornitoreLabel(f: { ragioneSociale: string | null; nome: string | null; cognome: string | null; codice: string }): string {
  if (f.ragioneSociale) return f.ragioneSociale;
  const nc = [f.nome, f.cognome].filter(Boolean).join(' ').trim();
  return nc || f.codice;
}

export default function LocazioneSchedaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermesso();
  const { societaAttiva, listaSocieta } = useSocieta();

  const isNuovo = id === 'nuovo';
  const numericId = !isNuovo && id ? parseInt(id, 10) : null;

  const [activeTab, setActiveTab] = useState<'info' | 'documenti' | 'storico'>('info');
  const [record, setRecord] = useState<Locazione | null>(null);
  const [form, setForm] = useState<FormState>(() =>
    emptyForm(societaAttiva ? String(societaAttiva.id) : '')
  );
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Selettori dipendenti dalla società
  const [sedi, setSedi] = useState<SedeMin[]>([]);
  const [fornitori, setFornitori] = useState<FornitoreMin[]>([]);

  // Upload state (tab documenti)
  const [uploadTipo, setUploadTipo] = useState<TipoDoc>('CONTRATTO');
  const [uploadDescrizione, setUploadDescrizione] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = can('management.locazioni', 'edit');
  const canDelete = can('management.locazioni', 'delete');

  // Per nuova locazione: societa è quella della topbar (se valorizzata) o quella scelta nel select (solo se SUPERADMIN "Tutte")
  const mostraSelectSocieta = isNuovo && !societaAttiva;

  // Hook: blocca cambio società quando si edita una scheda esistente
  useBloccoRecordContesto({
    recordSocietaId: record?.societaId,
    label: record
      ? `Locazione ${record.sede?.nome || ''} — ${fornitoreLabel(record.fornitore)}`.trim()
      : 'Locazione',
  });

  // Hook: avviso modifiche non salvate
  const isDirty = initialSnapshot !== '' && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, isNuovo ? 'Nuova locazione' : 'Scheda locazione');

  // Inizializzazione snapshot per nuovo
  useEffect(() => {
    if (isNuovo) {
      const f = emptyForm(societaAttiva ? String(societaAttiva.id) : '');
      setForm(f);
      setInitialSnapshot(JSON.stringify(f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNuovo, societaAttiva?.id]);

  // Carica record (edit)
  useEffect(() => {
    if (!numericId) return;
    void loadRecord(numericId);
  }, [numericId]);

  // Carica sedi e fornitori quando cambia la società
  useEffect(() => {
    if (!form.societaId) {
      setSedi([]);
      setFornitori([]);
      return;
    }
    void loadSediFornitori(form.societaId);
  }, [form.societaId]);

  // @api GET /api/locazioni/:id -> {"success":true,"data":{...locazione}}
  async function loadRecord(rid: number) {
    try {
      setIsLoading(true);
      const res = await apiFetch<{ success: boolean; data: Locazione }>(`/api/locazioni/${rid}`);
      setRecord(res.data);
      const f = recordToForm(res.data);
      setForm(f);
      setInitialSnapshot(JSON.stringify(f));
    } catch (err) {
      console.error('[LocazioneSchedaPage] load error', err);
      alert('Errore caricamento locazione');
      navigate('/management/locazioni');
    } finally {
      setIsLoading(false);
    }
  }

  // @api GET /api/sedi?societaId= -> {"sedi":[{...sede}]}
  // @api GET /api/nominativi?ruolo=FORNITORE&societaId= -> {"data":[{...nominativo}]}
  async function loadSediFornitori(sid: string) {
    try {
      const [sediRes, fornRes] = await Promise.all([
        apiFetch<{ sedi: SedeMin[] }>(`/api/sedi?societaId=${sid}`),
        apiFetch<{ data: FornitoreMin[]; total: number }>(`/api/nominativi?ruolo=FORNITORE&limit=500&societaId=${sid}`),
      ]);
      setSedi(sediRes.sedi || []);
      setFornitori(fornRes.data || []);
    } catch (err) {
      console.error('[LocazioneSchedaPage] sedi/fornitori', err);
    }
  }

  // @api GET /api/nominativi?ruolo=FORNITORE&societaId= -> {"data":[{...nominativo}]}
  async function loadFornitori(sid: string) {
    try {
      const fornRes = await apiFetch<{ data: FornitoreMin[]; total: number }>(
        `/api/nominativi?ruolo=FORNITORE&limit=500&societaId=${sid}`
      );
      setFornitori(fornRes.data || []);
    } catch (err) {
      console.error('[LocazioneSchedaPage] ricarica fornitori', err);
    }
  }

  function handleApriNuovoFornitore() {
    if (!form.societaId) return;
    window.open(
      `/anagrafiche/nominativi/nuovo?societaId=${form.societaId}`,
      '_blank',
      'noopener,noreferrer'
    );
  }

  async function handleRicaricaFornitori() {
    if (!form.societaId) return;
    await loadFornitori(form.societaId);
  }

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSocietaChange(newSid: string) {
    setForm((prev) => ({ ...prev, societaId: newSid, sedeId: '', fornitoreId: '' }));
  }

  // @api POST /api/locazioni {...form} -> {"success":true,"data":{"id":"number"}}
  // @api PUT /api/locazioni/:id {...form} -> {"success":true}
  async function handleSave() {
    if (!form.societaId || !form.sedeId || !form.fornitoreId || !form.dataInizio || !form.importoCanone) {
      alert('Compilare i campi obbligatori: società, sede, fornitore, data inizio, canone');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        societaId: parseInt(form.societaId, 10),
        sedeId: parseInt(form.sedeId, 10),
        fornitoreId: parseInt(form.fornitoreId, 10),
        dataInizio: form.dataInizio,
        durataAnniIniziale: parseInt(form.durataAnniIniziale || '6', 10),
        durataAnniProroga: parseInt(form.durataAnniProroga || '6', 10),
        preavvisoDisdettaMesi: parseInt(form.preavvisoDisdettaMesi || '12', 10),
        importoCanone: parseFloat(form.importoCanone),
        periodicitaPagamento: form.periodicitaPagamento,
        cauzioneImporto: form.cauzioneImporto ? parseFloat(form.cauzioneImporto) : null,
        cauzioneModalita: form.cauzioneModalita || null,
        cauzioneDataVersamento: form.cauzioneDataVersamento || null,
        cauzioneNote: form.cauzioneNote || null,
        rivalutazioneIstat: form.rivalutazioneIstat,
        rivalutazioneIstatPerc: form.rivalutazioneIstatPerc ? parseFloat(form.rivalutazioneIstatPerc) : null,
        disdettata: form.disdettata,
        dataDisdetta: form.dataDisdetta || null,
        noteDisdetta: form.noteDisdetta || null,
        note: form.note || null,
      };

      if (isNuovo) {
        const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/locazioni', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setInitialSnapshot(JSON.stringify(form));
        navigate(`/management/locazioni/${res.data.id}`);
      } else if (numericId) {
        await apiFetch(`/api/locazioni/${numericId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setInitialSnapshot(JSON.stringify(form));
        await loadRecord(numericId);
      }
    } catch (err: any) {
      console.error('[LocazioneSchedaPage] save error', err);
      alert(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  // @api DELETE /api/locazioni/:id -> {"success":true}
  async function handleDisattiva() {
    if (!numericId) return;
    if (!window.confirm('Disattivare questa locazione? Potrà essere riattivata modificandola.')) return;
    try {
      await apiFetch(`/api/locazioni/${numericId}`, { method: 'DELETE' });
      await loadRecord(numericId);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore disattivazione');
    }
  }

  // @api POST /api/locazioni/:id/documenti FormData{file,tipo,descrizione?} -> {"success":true,"data":{"id":"number"}}
  async function handleUploadDoc(e: React.FormEvent) {
    e.preventDefault();
    if (!numericId || !uploadFile) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('tipo', uploadTipo);
      if (uploadDescrizione.trim()) fd.append('descrizione', uploadDescrizione.trim());

      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/locazioni/${numericId}/documenti`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Errore upload');
      }
      setUploadFile(null);
      setUploadDescrizione('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadRecord(numericId);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore upload documento');
    } finally {
      setUploading(false);
    }
  }

  async function handleDownloadDoc(doc: DocumentoLocazione) {
    if (!numericId) return;
    try {
      await apiDownload(`/api/locazioni/${numericId}/documenti/${doc.id}/download`, doc.nomeFile);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore download');
    }
  }

  // @api DELETE /api/locazioni/:id/documenti/:docId -> {"success":true}
  async function handleDeleteDoc(doc: DocumentoLocazione) {
    if (!numericId) return;
    if (!window.confirm(`Eliminare definitivamente "${doc.nomeFile}"?`)) return;
    try {
      await apiFetch(`/api/locazioni/${numericId}/documenti/${doc.id}`, { method: 'DELETE' });
      await loadRecord(numericId);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Errore eliminazione documento');
    }
  }

  const dataFinePrima = useMemo(
    () => (form.dataInizio ? addYears(form.dataInizio, parseInt(form.durataAnniIniziale || '0', 10)) : '—'),
    [form.dataInizio, form.durataAnniIniziale]
  );
  const dataFineProroga = useMemo(
    () => (form.dataInizio
      ? addYears(form.dataInizio, parseInt(form.durataAnniIniziale || '0', 10) + parseInt(form.durataAnniProroga || '0', 10))
      : '—'),
    [form.dataInizio, form.durataAnniIniziale, form.durataAnniProroga]
  );

  // Etichetta società mostrata in header (priorità: record > societaAttiva > nome dalla lista per id selezionato)
  const societaLabel = useMemo(() => {
    if (record) return record.societa?.nome;
    if (societaAttiva) return societaAttiva.nome;
    if (form.societaId) {
      const s = listaSocieta.find((x) => String(x.id) === form.societaId);
      return s?.nome;
    }
    return null;
  }, [record, societaAttiva, form.societaId, listaSocieta]);

  const titolo = isNuovo
    ? 'Nuova locazione'
    : record
      ? `Locazione — ${record.sede?.nome || ''}`
      : 'Locazione';

  const formDisabled = !canEdit;

  return (
    <Layout title={titolo}>
      <BopBackLink to="/management/locazioni" label="Torna alle locazioni" />
      <div className="bop-page-header">
        <h1 className="bop-page-title">
          {titolo}
          {societaLabel && (
            <span className="bop-badge bop-badge-info bop-page-title__badge">
              {societaLabel}
            </span>
          )}
        </h1>
        <div className="bop-flex">
          {canEdit && (
            <button
              className="bop-btn-primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Salvataggio…' : 'Salva'}
            </button>
          )}
        </div>
      </div>

      {!isNuovo && (
        <div className="bop-tabs">
          <button
            className={`bop-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Informazioni
          </button>
          <button
            className={`bop-tab ${activeTab === 'documenti' ? 'active' : ''}`}
            onClick={() => setActiveTab('documenti')}
          >
            Documenti{record ? ` (${record.documenti.length})` : ''}
          </button>
          <button
            className={`bop-tab ${activeTab === 'storico' ? 'active' : ''}`}
            onClick={() => setActiveTab('storico')}
          >
            Storico
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="bop-card">
          <div className="bop-card__body">
            <div className="bop-empty-state">Caricamento…</div>
          </div>
        </div>
      ) : activeTab === 'info' ? (
        <>
          {/* Anagrafica */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Anagrafica</h2>
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                {mostraSelectSocieta && (
                  <div className="bop-form-group">
                    <label className="bop-label">Società *</label>
                    <select
                      className="bop-select"
                      value={form.societaId}
                      onChange={(e) => handleSocietaChange(e.target.value)}
                      disabled={formDisabled}
                    >
                      <option value="">Seleziona…</option>
                      {listaSocieta.map((s) => (
                        <option key={s.id} value={s.id}>{s.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="bop-form-group">
                  <label className="bop-label">Sede *</label>
                  <select
                    className="bop-select"
                    value={form.sedeId}
                    onChange={(e) => updateField('sedeId', e.target.value)}
                    disabled={!form.societaId || formDisabled}
                  >
                    <option value="">Seleziona…</option>
                    {sedi.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}{s.citta ? ` (${s.citta})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="bop-form-group">
                  <div className="bop-flex-between">
                    <label className="bop-label">Fornitore (locatore) *</label>
                    <div className="bop-flex">
                      <button
                        type="button"
                        className="bop-btn-ghost bop-btn-sm"
                        onClick={handleApriNuovoFornitore}
                        disabled={!form.societaId || formDisabled}
                        title="Crea nuovo fornitore in una nuova tab"
                      >
                        + Nuovo
                      </button>
                      <button
                        type="button"
                        className="bop-btn-ghost bop-btn-sm"
                        onClick={handleRicaricaFornitori}
                        disabled={!form.societaId || formDisabled}
                        title="Ricarica elenco fornitori"
                      >
                        ↻ Ricarica
                      </button>
                    </div>
                  </div>
                  <select
                    className="bop-select"
                    value={form.fornitoreId}
                    onChange={(e) => updateField('fornitoreId', e.target.value)}
                    disabled={!form.societaId || formDisabled}
                  >
                    <option value="">Seleziona…</option>
                    {fornitori.map((f) => (
                      <option key={f.id} value={f.id}>
                        {fornitoreLabel(f)} ({f.codice})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Durata */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Durata contratto</h2>
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data inizio *</label>
                  <input
                    type="date"
                    className="bop-input"
                    value={form.dataInizio}
                    onChange={(e) => updateField('dataInizio', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Durata iniziale (anni)</label>
                  <input
                    type="number"
                    min="1"
                    className="bop-input"
                    value={form.durataAnniIniziale}
                    onChange={(e) => updateField('durataAnniIniziale', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Proroga tacita (anni)</label>
                  <input
                    type="number"
                    min="0"
                    className="bop-input"
                    value={form.durataAnniProroga}
                    onChange={(e) => updateField('durataAnniProroga', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Preavviso disdetta (mesi)</label>
                  <input
                    type="number"
                    min="0"
                    className="bop-input"
                    value={form.preavvisoDisdettaMesi}
                    onChange={(e) => updateField('preavvisoDisdettaMesi', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data fine 1ª scadenza</label>
                  <input className="bop-input" value={dataFinePrima} disabled />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data fine dopo proroga</label>
                  <input className="bop-input" value={dataFineProroga} disabled />
                </div>
              </div>
            </div>
          </div>

          {/* Canone */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Canone</h2>
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Importo canone *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="bop-input"
                    value={form.importoCanone}
                    onChange={(e) => updateField('importoCanone', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Periodicità pagamento *</label>
                  <select
                    className="bop-select"
                    value={form.periodicitaPagamento}
                    onChange={(e) => updateField('periodicitaPagamento', e.target.value as Periodicita)}
                    disabled={formDisabled}
                  >
                    {(Object.keys(PERIODICITA_LABEL) as Periodicita[]).map((p) => (
                      <option key={p} value={p}>{PERIODICITA_LABEL[p]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Cauzione */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Cauzione</h2>
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Importo cauzione</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="bop-input"
                    value={form.cauzioneImporto}
                    onChange={(e) => updateField('cauzioneImporto', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Modalità versamento</label>
                  <select
                    className="bop-select"
                    value={form.cauzioneModalita}
                    onChange={(e) => updateField('cauzioneModalita', e.target.value as ModalitaCauzione | '')}
                    disabled={formDisabled}
                  >
                    <option value="">—</option>
                    {(Object.keys(MODALITA_CAUZIONE_LABEL) as ModalitaCauzione[]).map((m) => (
                      <option key={m} value={m}>{MODALITA_CAUZIONE_LABEL[m]}</option>
                    ))}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data versamento</label>
                  <input
                    type="date"
                    className="bop-input"
                    value={form.cauzioneDataVersamento}
                    onChange={(e) => updateField('cauzioneDataVersamento', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Note cauzione</label>
                  <input
                    type="text"
                    className="bop-input"
                    value={form.cauzioneNote}
                    onChange={(e) => updateField('cauzioneNote', e.target.value)}
                    disabled={formDisabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ISTAT */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Rivalutazione ISTAT</h2>
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">
                    <input
                      type="checkbox"
                      checked={form.rivalutazioneIstat}
                      onChange={(e) => updateField('rivalutazioneIstat', e.target.checked)}
                      disabled={formDisabled}
                    />
                    {' '}Rivalutazione ISTAT prevista
                  </label>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Percentuale ISTAT (es. 75, 100)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="bop-input"
                    value={form.rivalutazioneIstatPerc}
                    onChange={(e) => updateField('rivalutazioneIstatPerc', e.target.value)}
                    disabled={formDisabled || !form.rivalutazioneIstat}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Disdetta — solo in edit */}
          {!isNuovo && (
            <div className="bop-card">
              <div className="bop-card__header">
                <h2 className="bop-card__title">Disdetta</h2>
              </div>
              <div className="bop-card__body">
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">
                      <input
                        type="checkbox"
                        checked={form.disdettata}
                        onChange={(e) => updateField('disdettata', e.target.checked)}
                        disabled={formDisabled}
                      />
                      {' '}Locazione disdettata
                    </label>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Data disdetta</label>
                    <input
                      type="date"
                      className="bop-input"
                      value={form.dataDisdetta}
                      onChange={(e) => updateField('dataDisdetta', e.target.value)}
                      disabled={formDisabled || !form.disdettata}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Note disdetta</label>
                    <input
                      type="text"
                      className="bop-input"
                      value={form.noteDisdetta}
                      onChange={(e) => updateField('noteDisdetta', e.target.value)}
                      disabled={formDisabled || !form.disdettata}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Note</h2>
            </div>
            <div className="bop-card__body">
              <textarea
                className="bop-input"
                rows={4}
                value={form.note}
                onChange={(e) => updateField('note', e.target.value)}
                disabled={formDisabled}
              />
            </div>
          </div>

          {/* Riepilogo + azione disattiva — solo in edit */}
          {!isNuovo && record && (
            <div className="bop-card">
              <div className="bop-card__header">
                <h2 className="bop-card__title">Riepilogo</h2>
              </div>
              <div className="bop-card__body">
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Canone {PERIODICITA_LABEL[record.periodicitaPagamento]}</label>
                    <div>{formatCurrency(Number(record.importoCanone))}</div>
                  </div>
                  {record.cauzioneImporto != null && (
                    <div className="bop-form-group">
                      <label className="bop-label">Cauzione versata</label>
                      <div>{formatCurrency(Number(record.cauzioneImporto))}</div>
                    </div>
                  )}
                </div>
                {canDelete && record.attivo && (
                  <div className="bop-flex bop-mt-3">
                    <button className="bop-btn-danger" onClick={handleDisattiva}>
                      Disattiva locazione
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : activeTab === 'documenti' && record ? (
        <>
          {canEdit && (
            <div className="bop-card">
              <div className="bop-card__header">
                <h2 className="bop-card__title">Carica nuovo documento</h2>
              </div>
              <div className="bop-card__body">
                <form onSubmit={handleUploadDoc}>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Tipo *</label>
                      <select
                        className="bop-select"
                        value={uploadTipo}
                        onChange={(e) => setUploadTipo(e.target.value as TipoDoc)}
                      >
                        {(Object.keys(TIPO_DOC_LABEL) as TipoDoc[]).map((t) => (
                          <option key={t} value={t}>{TIPO_DOC_LABEL[t]}</option>
                        ))}
                      </select>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Descrizione</label>
                      <input
                        type="text"
                        className="bop-input"
                        value={uploadDescrizione}
                        onChange={(e) => setUploadDescrizione(e.target.value)}
                        placeholder="es. Contratto registrato il 12/05/2026"
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">File PDF *</label>
                      <input
                        type="file"
                        accept="application/pdf"
                        className="bop-input"
                        ref={fileInputRef}
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      />
                    </div>
                  </div>
                  <div className="bop-flex bop-mt-3">
                    <button className="bop-btn-primary" type="submit" disabled={uploading || !uploadFile}>
                      {uploading ? 'Caricamento…' : 'Carica documento'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Documenti caricati</h2>
            </div>
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>File</th>
                  <th>Descrizione</th>
                  <th>Caricato da</th>
                  <th>Data</th>
                  <th className="bop-text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {record.documenti.length === 0 ? (
                  <tr><td colSpan={6}><div className="bop-empty-state">Nessun documento caricato</div></td></tr>
                ) : (
                  record.documenti.map((d) => (
                    <tr key={d.id}>
                      <td><span className="bop-badge bop-badge-info">{TIPO_DOC_LABEL[d.tipo]}</span></td>
                      <td>{d.nomeFile}</td>
                      <td>{d.descrizione || '—'}</td>
                      <td>{d.uploadedByName || '—'}</td>
                      <td>{new Date(d.uploadedAt).toLocaleDateString('it-IT')}</td>
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Download />, label: 'Scarica', onClick: () => handleDownloadDoc(d) },
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => handleDeleteDoc(d), variant: 'danger', hidden: !canEdit },
                        ]} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : activeTab === 'storico' && record ? (
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Storico modifiche</h2>
          </div>
          <table className="bop-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Utente</th>
                <th>Azione</th>
                <th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {record.log.length === 0 ? (
                <tr><td colSpan={4}><div className="bop-empty-state">Nessuna voce di log</div></td></tr>
              ) : (
                record.log.map((l) => (
                  <tr key={l.id}>
                    <td>{new Date(l.createdAt).toLocaleString('it-IT')}</td>
                    <td>{l.utenteName || '—'}</td>
                    <td>{l.azione}</td>
                    <td className="bop-text-muted">{l.dettaglio || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
